import { Toaster } from "@/components/ui/sonner"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import NavigationTracker from '@/lib/NavigationTracker'
import { pagesConfig } from './pages.config'
import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import PageTransition from '@/lib/PageTransition';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { LoyaltyProvider } from '@/lib/LoyaltyContext';
import { OrderPageProvider } from '@/lib/OrderPageContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ProtectedRoute from '@/components/ProtectedRoute';
import FullscreenPageWrapper from './components/FullscreenPageWrapper';
import OnboardingGuard from './components/OnboardingGuard';
import LoginVersionGuard from './components/LoginVersionGuard';
import PageErrorBoundary from './components/PageErrorBoundary';

// Eager: the two screens an unauthenticated visitor can be redirected straight
// into. Lazy-loading these would add a round trip to the sign-in path.
import Login from './pages/Login';
import Register from './pages/Register';
import Splash from './pages/Splash';
import AuthCardShell from '@/components/auth/AuthCardShell';

import lazyRetry from '@/lib/lazyRetry';
import { isCustomDomain } from '@/components/AppStoreBadges';
import AppHome from './pages/AppHome';

// Everything else is code-split so its JavaScript is only downloaded when the
// route is actually visited, instead of shipping every page to every visitor in
// one bundle. The <Suspense> boundary below renders while a chunk loads.
const ForgotPassword = lazyRetry(() => import('./pages/ForgotPassword'));
const ResetPassword = lazyRetry(() => import('./pages/ResetPassword'));
const PaymentCards = lazyRetry(() => import('./pages/PaymentCards'));
const ContactUs = lazyRetry(() => import('./pages/ContactUs'));
const BookATable = lazyRetry(() => import('./pages/BookATable'));
const NotificationCenter = lazyRetry(() => import('./pages/NotificationCenter'));
const Allergens = lazyRetry(() => import('./pages/Allergens'));
const Menus = lazyRetry(() => import('./pages/Menus'));
const CheckGiftCardBalance = lazyRetry(() => import('./pages/CheckGiftCardBalance'));

const Welcome = lazyRetry(() => import('./pages/Welcome'));
const Cookies = lazyRetry(() => import('./pages/Cookies'));
const GDPR = lazyRetry(() => import('./pages/GDPR'));
const PrivacyPolicy = lazyRetry(() => import('./pages/PrivacyPolicy'));
const Security = lazyRetry(() => import('./pages/Security'));
const TermsAndConditions = lazyRetry(() => import('./pages/TermsAndConditions'));
const TrackOrder = lazyRetry(() => import('./pages/TrackOrder'));
const Favourites = lazyRetry(() => import('./pages/Favourites'));
const BuyGiftCard = lazyRetry(() => import('./pages/BuyGiftCard'));
const GroupGiftContribute = lazyRetry(() => import('./pages/GroupGiftContribute'));
const SideMenu = lazyRetry(() => import('./pages/SideMenu'));
const HowToRegister = lazyRetry(() => import('./pages/HowToRegister'));
const Onboarding = lazyRetry(() => import('./pages/Onboarding'));
const Events = lazyRetry(() => import('./pages/Events'));
const EventDetail = lazyRetry(() => import('./pages/EventDetail'));
const EventCheckout = lazyRetry(() => import('./pages/EventCheckout'));
const EventBookings = lazyRetry(() => import('./pages/EventBookings'));
const EventSalesReportPage = lazyRetry(() => import('./pages/EventSalesReportPage'));
const PauseOrders = lazyRetry(() => import('./pages/PauseOrders'));
const TwelveDays = lazyRetry(() => import('./pages/TwelveDays'));

// Shows the Splash screen with the current path as the `next` redirect param
const SplashWithNext = () => {
  const loc = window.location;
  const next = encodeURIComponent(loc.pathname + loc.search);
  return <Navigate to={`/Splash?next=${next}`} replace />;
};

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>{children}</Layout>
  : <>{children}</>;


// Shown for the moment a code-split page's chunk is being downloaded. Matches
// the app's existing loading spinner so the transition doesn't look different.
const PageChunkLoader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white">
    <div className="w-8 h-8 border-4 rounded-full animate-spin border-brand-navy-deep border-t-transparent"></div>
  </div>
);

const AuthenticatedApp = () => {
  const { user, isLoadingAuth, isLoadingPublicSettings, authError } = useAuth();

  if (isLoadingPublicSettings) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 rounded-full animate-spin border-brand-navy-deep border-t-transparent"></div>
      </div>
    );
  }

  if (authError?.type === 'user_not_registered') {
    return <UserNotRegisteredError />;
  }

  return (
    <Suspense fallback={<PageChunkLoader />}>
    <Routes>
      {/* Public auth routes */}
      <Route element={<AuthCardShell />}>
        <Route path="/login" element={<Login />} />
        <Route path="/Register" element={<Register />} />
      </Route>
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/Splash" element={<PageTransition><Splash /></PageTransition>} />
      <Route path="/Welcome" element={<PageTransition><Welcome /></PageTransition>} />

      {/* ── PUBLIC routes (no auth required) ── */}
      <Route path="/" element={isCustomDomain()
        ? <PageTransition><LayoutWrapper currentPageName={mainPageKey}><MainPage /></LayoutWrapper></PageTransition>
        : <PageTransition><LayoutWrapper currentPageName="Home"><AppHome /></LayoutWrapper></PageTransition>} />
      <Route path="/AppHome" element={<PageTransition><LayoutWrapper currentPageName="Home"><AppHome /></LayoutWrapper></PageTransition>} />
      {Object.entries(Pages)
        .filter(([path]) => !['Checkout', 'Account', 'AdminSettings'].includes(path))
        .map(([path, Page]) => (
          <Route key={path} path={`/${path}`} element={<PageTransition><LayoutWrapper currentPageName={path}><Page /></LayoutWrapper></PageTransition>} />
        ))}
      <Route path="/ContactUs" element={<PageTransition><LayoutWrapper currentPageName="ContactUs"><ContactUs /></LayoutWrapper></PageTransition>} />
      <Route path="/BookATable" element={<PageTransition><FullscreenPageWrapper currentPageName="BookATable"><BookATable /></FullscreenPageWrapper></PageTransition>} />
      <Route path="/GiftCards" element={<Navigate to="/BuyGiftCard" replace />} />
      <Route path="/Allergens" element={<PageTransition><LayoutWrapper currentPageName="Allergens"><Allergens /></LayoutWrapper></PageTransition>} />
      <Route path="/Menus" element={<PageTransition><LayoutWrapper currentPageName="Menus"><Menus /></LayoutWrapper></PageTransition>} />
      <Route path="/CheckGiftCardBalance" element={<PageTransition><LayoutWrapper currentPageName="CheckGiftCardBalance"><CheckGiftCardBalance /></LayoutWrapper></PageTransition>} />
      <Route path="/Cookies" element={<PageTransition><LayoutWrapper currentPageName="Cookies"><Cookies /></LayoutWrapper></PageTransition>} />
      <Route path="/GDPR" element={<PageTransition><LayoutWrapper currentPageName="GDPR"><GDPR /></LayoutWrapper></PageTransition>} />
      <Route path="/PrivacyPolicy" element={<PageTransition><LayoutWrapper currentPageName="PrivacyPolicy"><PrivacyPolicy /></LayoutWrapper></PageTransition>} />
      <Route path="/Security" element={<PageTransition><LayoutWrapper currentPageName="Security"><Security /></LayoutWrapper></PageTransition>} />
      <Route path="/TermsAndConditions" element={<PageTransition><LayoutWrapper currentPageName="TermsAndConditions"><TermsAndConditions /></LayoutWrapper></PageTransition>} />
      <Route path="/TrackOrder" element={<PageTransition><LayoutWrapper currentPageName="TrackOrder"><TrackOrder /></LayoutWrapper></PageTransition>} />
      <Route path="/Favourites" element={<PageTransition><LayoutWrapper currentPageName="Favourites"><Favourites /></LayoutWrapper></PageTransition>} />
      <Route path="/Events" element={<PageTransition><LayoutWrapper currentPageName="Events"><Events /></LayoutWrapper></PageTransition>} />
      <Route path="/Events/:itemId" element={<PageTransition><LayoutWrapper currentPageName="Events"><EventDetail /></LayoutWrapper></PageTransition>} />
      <Route path="/EventCheckout/:itemId" element={<PageTransition><LayoutWrapper currentPageName="Events"><EventCheckout /></LayoutWrapper></PageTransition>} />
      <Route path="/TwelveDays" element={<PageTransition><LayoutWrapper currentPageName="TwelveDays"><TwelveDays /></LayoutWrapper></PageTransition>} />
      <Route path="/SideMenu" element={<PageTransition><SideMenu /></PageTransition>} />
      <Route path="/HowToRegister" element={<PageTransition><HowToRegister /></PageTransition>} />
      <Route path="/GroupGift/:poolId" element={<PageTransition><PageErrorBoundary><GroupGiftContribute /></PageErrorBoundary></PageTransition>} />

      {/* ── PROTECTED routes (auth required) ── */}
      <Route element={<ProtectedRoute unauthenticatedElement={<SplashWithNext />} />}>
        {/* If authenticated but onboarding incomplete, redirect to Onboarding */}
        <Route element={<OnboardingGuard />}>
          <Route path="/Checkout" element={<PageTransition><LayoutWrapper currentPageName="Checkout"><Pages.Checkout /></LayoutWrapper></PageTransition>} />
          <Route path="/Account" element={<PageTransition><LayoutWrapper currentPageName="Account"><Pages.Account /></LayoutWrapper></PageTransition>} />
          <Route path="/AdminSettings" element={<PageTransition><LayoutWrapper currentPageName="AdminSettings"><Pages.AdminSettings /></LayoutWrapper></PageTransition>} />
          <Route path="/PaymentCards" element={<PageTransition><LayoutWrapper currentPageName="PaymentCards"><PaymentCards /></LayoutWrapper></PageTransition>} />
          <Route path="/NotificationCenter" element={<PageTransition><LayoutWrapper currentPageName="NotificationCenter"><NotificationCenter /></LayoutWrapper></PageTransition>} />
          <Route path="/EventBookings" element={<PageTransition><LayoutWrapper currentPageName="EventBookings"><EventBookings /></LayoutWrapper></PageTransition>} />
          <Route path="/PauseOrders" element={<PageTransition><LayoutWrapper currentPageName="PauseOrders"><PauseOrders /></LayoutWrapper></PageTransition>} />
          <Route path="/EventSalesReport" element={<PageTransition><LayoutWrapper currentPageName="Sales Report"><EventSalesReportPage /></LayoutWrapper></PageTransition>} />
        </Route>

        {/* Gift card purchase requires a signed-in user — the backend now rejects
            unauthenticated purchase, activation and delivery requests. */}
        <Route path="/BuyGiftCard" element={<PageTransition><LayoutWrapper currentPageName="BuyGiftCard"><BuyGiftCard /></LayoutWrapper></PageTransition>} />

        <Route path="/Onboarding" element={<PageTransition><Onboarding /></PageTransition>} />
      </Route>

      <Route path="*" element={<PageNotFound />} />
    </Routes>
    </Suspense>
  );
};


function App() {
  return (
    <AuthProvider>
      <LoyaltyProvider>
        <OrderPageProvider>
          <QueryClientProvider client={queryClientInstance}>
            <Router>
              <NavigationTracker />
              <LoginVersionGuard />
              <AuthenticatedApp />
            </Router>
            <Toaster />
          </QueryClientProvider>
        </OrderPageProvider>
      </LoyaltyProvider>
    </AuthProvider>
  )
}

export default App